import { MindmapNodeDirection, MindmapNodeOrientation } from "shared/datamodel/schemas/mindmap";
import { MindmapNodeTree } from "frontend/utils/mindmap-utils";
import { Point } from "frontend/utils/math-utils";

export type DropTargetType = "child" | "between";

export interface DropTarget {
  type: DropTargetType;
  x: number;
  y: number;
  direction?: MindmapNodeDirection; // For root node drops or explicit direction
  distance: number;
  // For "child" type
  targetNode?: MindmapNodeTree;
  // For "between" type
  parentNode?: MindmapNodeTree;
  prevSibling?: MindmapNodeTree; // undefined means before first child
  nextSibling?: MindmapNodeTree; // undefined means after last child
}

export interface DragState {
  draggedNodeId: string;
  descendantIds: string[];
  dropTarget: DropTarget | null;
  draggedNodeOffset: Point; // Offset from mouse to node top-left
}

export const DRAG_DROP_THRESHOLD = 50 * 50; // Squared distance threshold

/**
 * Calculates the closest drop target for a given mouse position
 * Returns a single drop target or null if none found
 *
 * Priority:
 * 1. If mouse is inside a node's bounding box → that node is the target (as child)
 * 2. If mouse is between nodes → closest "between-siblings" position
 *
 * Filters out invalid targets where the drop would result in no change
 * (same parent, same direction, same position)
 */
export function calculateDropTarget(
  mousePos: Point, // Local coordinates relative to mindmap root
  tree: MindmapNodeTree,
  draggedNodeId: string,
  descendantIds: string[],
  // Current state of the first dragged node (to filter out no-op drops)
  currentParentId?: string,
  currentDirection?: MindmapNodeDirection,
  currentPrevSiblingId?: string,
  currentNextSiblingId?: string
): DropTarget | null {
  const orientation = tree.orientation;
  const isVertical = orientation === MindmapNodeOrientation.VERTICAL;

  let bestTarget: DropTarget | null = null;
  let minDistance = Number.MAX_VALUE;

  // Helper to check if point is inside node's bounding box
  const isPointInsideNode = (node: MindmapNodeTree, point: Point, threshold = 0): boolean => {
    return (
      point.x >= node.x - threshold &&
      point.x <= node.x + node.width + threshold &&
      point.y >= node.y - threshold &&
      point.y <= node.y + node.height + threshold
    );
  };

  // Helper to calculate distance to a point
  const distanceToPoint = (x: number, y: number): number => {
    return (mousePos.x - x) ** 2 + (mousePos.y - y) ** 2;
  };

  // Helper to update best target if closer
  const updateBestTarget = (target: DropTarget) => {
    const dist = distanceToPoint(target.x, target.y);
    if (dist < minDistance && dist < DRAG_DROP_THRESHOLD) {
      minDistance = dist;
      bestTarget = { ...target, distance: dist };
    }
  };

  // First pass: Check if mouse is inside any node's bounding box
  const traverseForInsideNode = (node: MindmapNodeTree) => {
    // Skip if node is part of dragged subtree
    if (node.id === draggedNodeId || descendantIds.includes(node.id)) {
      return;
    }

    if (isPointInsideNode(node, mousePos, -10)) {
      // Mouse is inside this node - this is the target
      const centerX = node.x + node.width / 2;
      const centerY = node.y + node.height / 2;

      // Determine direction for parent nodes
      let direction: MindmapNodeDirection | undefined;
      if (node.parentId === undefined) {
        // Root node: determine direction based on mouse position relative to center
        // Notice that orientation is confusing in mindmap - horizontal means vertical and vice versa.
        // Just a bad decision that is cemented for ever in our data.
        if (orientation === MindmapNodeOrientation.HORIZONTAL) {
          direction = mousePos.y < centerY ? MindmapNodeDirection.LEADING : MindmapNodeDirection.TRAILING;
        } else {
          direction = mousePos.x < centerX ? MindmapNodeDirection.LEADING : MindmapNodeDirection.TRAILING;
        }
      } else {
        // Non-root node: inherit direction from the node
        direction = node.direction;
      }

      bestTarget = {
        type: "child",
        x: centerX,
        y: centerY,
        direction,
        distance: 0, // Inside node, so distance is effectively 0
        targetNode: node,
      };
      return; // Found target, can stop searching
    }

    // Recurse to children
    if (node.children) {
      for (const child of node.children) {
        traverseForInsideNode(child);
        if (bestTarget) return; // Early exit if target found
      }
    }
  };

  traverseForInsideNode(tree);

  // If we found a target inside a node, validate before returning
  if (bestTarget) {
    const childTarget: DropTarget = bestTarget; // Type assertion to help TypeScript
    if (childTarget.type === "child") {
      // Check if dropping as child would result in no change
      if (currentParentId !== undefined && currentDirection !== undefined) {
        const targetParentId = childTarget.targetNode?.id;
        const targetDirection = childTarget.direction;
        if (targetParentId === currentParentId && targetDirection === currentDirection) {
          // Would result in no change - don't return this target, continue to "between" search
          bestTarget = null;
        } else {
          return childTarget; // Valid target, return it
        }
      } else {
        return childTarget; // No current state to compare, return the target
      }
    }
  }

  // Second pass: Check for "between-siblings" positions
  // Reset search state
  bestTarget = null;
  minDistance = Number.MAX_VALUE;

  const traverseForBetweenSiblings = (parentNode: MindmapNodeTree) => {
    // Process children to find gaps between siblings
    if (parentNode.children && parentNode.children.length > 0) {
      const validChildren = parentNode.children.filter(
        (child) => child.id !== draggedNodeId && !descendantIds.includes(child.id)
      );

      // If no valid children after filtering, skip
      if (validChildren.length === 0) {
        return;
      }

      for (let i = 0; i <= validChildren.length; i++) {
        const prevSibling = i > 0 ? validChildren[i - 1] : undefined;
        const nextSibling = i < validChildren.length ? validChildren[i] : undefined;

        // Calculate position for this gap
        let gapPos: Point;
        let direction: MindmapNodeDirection | undefined;

        if (!prevSibling && nextSibling) {
          // Before first child
          if (!isVertical) {
            gapPos = { x: nextSibling.x, y: nextSibling.y + nextSibling.height / 2 };
          } else {
            gapPos = { x: nextSibling.x + nextSibling.width / 2, y: nextSibling.y };
          }
          direction = nextSibling.direction;
        } else if (prevSibling && !nextSibling) {
          // After last child
          if (!isVertical) {
            gapPos = { x: prevSibling.x + prevSibling.width, y: prevSibling.y + prevSibling.height / 2 };
          } else {
            gapPos = { x: prevSibling.x + prevSibling.width / 2, y: prevSibling.y + prevSibling.height };
          }
          direction = prevSibling.direction;
        } else if (prevSibling && nextSibling) {
          // Between two siblings - use midpoint
          if (!isVertical) {
            const midX = (prevSibling.x + prevSibling.width + nextSibling.x) / 2;
            const midY = (prevSibling.y + prevSibling.height / 2 + nextSibling.y + nextSibling.height / 2) / 2;
            gapPos = { x: midX, y: midY };
          } else {
            const midX = (prevSibling.x + prevSibling.width / 2 + nextSibling.x + nextSibling.width / 2) / 2;
            const midY = (prevSibling.y + prevSibling.height + nextSibling.y) / 2;
            gapPos = { x: midX, y: midY };
          }
          direction = prevSibling.direction; // Use direction from either sibling (they should be the same)
        } else {
          continue; // Skip if both are undefined
        }

        updateBestTarget({
          type: "between",
          x: gapPos.x,
          y: gapPos.y,
          direction,
          distance: 0, // Will be calculated in updateBestTarget
          parentNode,
          prevSibling,
          nextSibling,
        });
      }
    }

    // Recurse to children
    if (parentNode.children) {
      parentNode.children.forEach(traverseForBetweenSiblings);
    }
  };

  traverseForBetweenSiblings(tree);

  // Filter out targets that would result in no change
  if (!bestTarget || currentParentId === undefined || currentDirection === undefined) {
    return bestTarget;
  }

  // At this point, bestTarget is non-null and we have current state to compare
  const target: DropTarget = bestTarget; // Explicit type to help TypeScript

  // Note: "child" types are already validated in the first pass above
  // Only need to validate "between" types here
  if (target.type === "between") {
    // For "between" type: check if same parent, direction, AND position
    const targetParentId = target.parentNode?.id;
    const targetDirection = target.direction;
    const targetPrevSiblingId = target.prevSibling?.id;
    const targetNextSiblingId = target.nextSibling?.id;

    if (
      targetParentId === currentParentId &&
      targetDirection === currentDirection &&
      targetPrevSiblingId === currentPrevSiblingId &&
      targetNextSiblingId === currentNextSiblingId
    ) {
      return null; // Would result in no change (same position)
    }
  }

  return target;
}
